import { Injectable } from '@angular/core';
import { ID, transaction } from '@datorama/akita';
import { NgEntityService } from '@datorama/akita-ng-entity-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { normalize, schema } from 'normalizr';
import { tap } from 'rxjs/operators';

import { SocialMedia } from '../../model/socialmedia';
import { UrlConfig } from '../../model/urlconfig';
import { HttpOptions } from '../../service/generic-akita-http.service';
import { WebsocketService } from '../../service/websocket.service';
import { SocialMediaUsersStore } from '../state/social-media-users.store';
import { SocialMediaState, SocialMediaStore } from '../state/social-media.store';
import { AkitaSocialMediaHttpService } from './akita-http-socialmedia.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class SocialMediaService extends NgEntityService<SocialMediaState> implements UrlConfig {

  constructor(
    protected store: SocialMediaStore,
    private socialMediaHttpService: AkitaSocialMediaHttpService,
    private socialMediaUserStore: SocialMediaUsersStore,
    private webSocketService: WebsocketService,
  ) {
    super(store);

    this.webSocketService.getDataStream('SocialMedia')
      .pipe(untilDestroyed(this))
      .subscribe((data: SocialMedia[]) => {
        const resp = getSocialMedia(data);
        if (resp.entities.SocialMediaUser) {
          const smUser = Object.values(resp.entities.SocialMediaUser);
          this.socialMediaUserStore.upsertMany(smUser);
        }
        if (resp.entities.SocialMedia) {
          const sm = Object.values(resp.entities.SocialMedia);
          this.store.upsertMany(sm);
        }
      });
  }
  updateEntitiesUrl(newUrl: string): void {
    this.socialMediaHttpService.setEntitiesUrl(newUrl);
  }

  getAll() {
    this.socialMediaHttpService.getAll().subscribe(sm => this.store.add(sm));
  }

  getById(id: ID) {
    this.socialMediaHttpService.loadById(id).subscribe(sm => this.store.add(sm));
  }

  getForCurrentSearchIds(searchIds: string[]) {
    if (searchIds.length <= 0) { return; }

    const queryIds = searchIds.map(i => `'${i}'`).join(',');
    const queryString = `searchIds==${queryIds}`;

    this.socialMediaHttpService.executeQuery(queryString, 'SocialMedia', true)
      .subscribe(data => {
        const resp = getSocialMedia(data);

        if (resp.entities.SocialMediaUser) {
          const smUser = Object.values(resp.entities.SocialMediaUser);
          this.socialMediaUserStore.upsertMany(smUser);
        }

        if (resp.entities.SocialMedia) {
          const sm = Object.values(resp.entities.SocialMedia);
          this.store.add(sm);
        }
      });
  }

  getLatestMaxItems(maxItems: number, searchIds: string[]) {
    if (searchIds.length <= 0) { return; }

    const queryIds = searchIds.map(i => `'${i}'`).join(',');
    const queryString = `searchIds==${queryIds}`;

    const options: HttpOptions = {
      params: {
        q: queryString,
        type: 'SocialMedia',
        options: 'keyValues',
        orderBy: '!dateIssued'
      }
    };

    this.socialMediaHttpService.getMaxItems(maxItems, options).subscribe(data => {
      const resp = getSocialMedia(data);

      if (resp.entities.SocialMediaUser) {
        const smUser = Object.values(resp.entities.SocialMediaUser);
        this.socialMediaUserStore.upsertMany(smUser);
      }

      if (resp.entities.SocialMedia) {
        const sm = Object.values(resp.entities.SocialMedia);
        this.store.add(sm);
      }
    });
  }

  getForPage(page: number) {
    this.store.setLoading(true);

    const limit = 100;
    const offset = (page - 1) * limit;
    let hasMore = true;

    this.socialMediaHttpService.getNextXItems(offset, limit).pipe(
      tap(items => {
        if (items.length <= 0) { hasMore = false; }
      })
    ).subscribe(
      res => this.updateSocialMedia(res, page, hasMore),
      err => console.log('Error in get forPage', err)
    );
  }

  @transaction()
  updateSocialMedia(response: SocialMedia[], currentPage: number, hasMore: boolean) {
    const resp = getSocialMedia(response);

    if (resp.entities.SocialMediaUser) {
      const smUser = Object.values(resp.entities.SocialMediaUser);
      this.socialMediaUserStore.add(smUser);
    }
    if (resp.entities.SocialMedia) {
      const sm = Object.values(resp.entities.SocialMedia);
      this.store.add(sm);
    }

    const nextPage = currentPage + 1;
    this.store.update({ page: nextPage, hasMore });
    this.store.setLoading(false);
  }
}

const user = new schema.Entity('SocialMediaUser');
const socialMedia = new schema.Entity('SocialMedia', {
  createdBy: user
});

export const getSocialMedia = (serverResponse: any) => normalize(serverResponse, [socialMedia]);
